// Audit G1: a wine rating goes through the submit_wine_review RPC (migration
// 226), never a direct wine_reviews_v2 write. The server checks the seat's
// claim token, that the room is on this wine's rating step, and bounds the
// rating (0–5 on a 0.1 grid) and the note (500 characters).
//
// Errors worth knowing at the call site:
//   42501 — not your seat, or ratings for this wine have closed
//   22023 — the rating or note is out of range, or the wine isn't rated here
// Both are terminal (utils/requestQueue TERMINAL_ERROR_CODES): retrying won't
// change the answer.

import { supabase } from '@tasted/utils/supabase';
import { claimTokenStorage } from './playerStorage';

export const WINE_REVIEW_RPC = 'submit_wine_review';
export const WINE_REVIEW_NOTE_MAX = 500;

/**
 * RPC args for one rating. The token is read from claimTokenStorage unless
 * the caller passes one.
 */
export function buildWineReviewArgs({ sessionCode, playerId, wineId, rating, note, claimToken }) {
  const id = playerId ? String(playerId) : null;
  return {
    p_session_id: String(sessionCode),
    p_player_id: id,
    p_claim_token: (claimToken ?? claimTokenStorage.get(id)) || null,
    p_wine_id: String(wineId),
    p_rating: rating,
    p_note: note ? String(note) : null,
  };
}

/**
 * The durable-queue op for the same write. The queue executor refreshes the
 * token at replay time, so a rejoin between enqueue and replay still works.
 */
export function wineReviewQueueOp(args) {
  return { rpc: WINE_REVIEW_RPC, args };
}

/**
 * An old client queued ratings as `{ table: 'wine_reviews_v2', row }`. After
 * 227 that direct write is refused, so the executor rewrites it into the RPC.
 * Returns null for any other op.
 */
export function legacyWineReviewOpToRpc(op) {
  if (!op || op.rpc || op.table !== 'wine_reviews_v2' || !op.row) return null;
  const { session_id, user_id, wine_id, rating, note } = op.row;
  return wineReviewQueueOp(buildWineReviewArgs({
    sessionCode: session_id, playerId: user_id, wineId: wine_id, rating, note,
  }));
}

/**
 * True when the server refused because the room has moved past this wine's
 * rating ("ratings for this wine are closed", 42501). 42501 alone also means
 * "not your seat", which keeps the ordinary error.
 */
export function isRatingsClosedError(error) {
  return error?.code === '42501' && /closed/i.test(String(error?.message ?? ''));
}

/** Submit one rating. Returns `{ data, error }` in the supabase shape. */
export async function submitWineReview(params) {
  const res = await supabase.rpc(WINE_REVIEW_RPC, buildWineReviewArgs(params));
  return { data: res?.data ?? null, error: res?.error ?? null };
}
